import { useState, useEffect } from "react";
import {
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Calendar,
  Clock,
  Ellipsis,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { authAPI } from "../../services/api";

export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [activeStatusDropdowns, setActiveStatusDropdowns] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState("desc"); // 'asc' or 'desc'
  const [loading, setLoading] = useState(true);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [toast, setToast] = useState({ show: false, type: '', message: '' });

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => {
      setToast({ show: false, type: '', message: '' });
    }, 3000);
  };

  // Fetch orders from API
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const result = await authAPI.getFarmerOrders();
        
        if (result.success) {
          // Transform API data to match our component structure
          const transformedOrders = result.data.orders.map(order => ({
            id: `ORD-${order.order_id}`,
            orderId: order.order_id,
            date: order.created_at,
            status: order.status,
            farmer: order.buyer.name,
            buyerEmail: order.buyer.email,
            buyerPhone: order.buyer.phone,
            total: parseFloat(order.order_total),
            farmerEarnings: parseFloat(order.farmer_earnings),
            shippingAddress: order.shipping_address,
            phone: order.phone,
            sellerConfirmed: order.seller_transaction_complete_confirmation === 1,
            buyerConfirmed: order.buyer_transaction_complete_confirmation === 1,
            items: order.items.map(item => ({
              name: item.product_name || 'Product',
              description: item.description || '',
              quantity: item.quantity,
              price: parseFloat(item.price),
              total: parseFloat(item.total),
              unit: 'unit' // Default unit since API doesn't provide it
            }))
          }));
          
          setOrders(transformedOrders);
          setTotalEarnings(result.data.total_earnings || 0);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
        showToast('error', 'Failed to load orders');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };
  
  const toggleStatusDropdown = (orderId, e) => {
    e.stopPropagation(); 

    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: true,
    }));

    
    setActiveStatusDropdowns((prev) => {
     
      const newState = {};

      newState[orderId] = !prev[orderId];
      return newState;
    });
  };
 
  const changeOrderStatus = (orderId, newStatus, e) => {
    e.stopPropagation();
    setOrders((prevOrders) =>
      prevOrders.map((order) =>
        order.id === orderId ? { ...order, status: newStatus } : order
      )
    );
    
    setActiveStatusDropdowns((prev) => ({
      ...prev,
      [orderId]: false,
    }));
  };

  const handleConfirmSeller = (order) => {
    setSelectedOrder(order);
    setShowConfirmModal(true);
  };

  const confirmSellerTransaction = async () => {
    if (!selectedOrder) return;

    try {
      setConfirming(true);
      const result = await authAPI.confirmSellerTransaction(selectedOrder.orderId);
      
      if (result.success) {
        showToast('success', 'Seller transaction confirmed successfully');
        
        // Update the order in local state
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.id === selectedOrder.id 
              ? { ...order, sellerConfirmed: true }
              : order
          )
        );
        
        setShowConfirmModal(false);
        setSelectedOrder(null);
      } else {
        showToast('error', result.message || 'Failed to confirm transaction');
      }
    } catch (error) {
      console.error('Error confirming seller transaction:', error);
      showToast('error', 'Failed to confirm transaction');
    } finally {
      setConfirming(false);
    }
  };

  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.farmer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.items.some((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesStatus =
      statusFilter === "All" || order.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
  });

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "delivered":
        return "order-status-app delivered-app";
      case "processing":
        return "order-status-app processing-app";
      case "canceled":
        return "order-status-app canceled-app";
      case "pending":
        return "order-status-app pending-app";
      default:
        return "order-status-app";
    }
  };

  const handleClickOutside = () => {
    setActiveStatusDropdowns({});
  };

  if (loading) {
    return (
      <div className="orders-container-app">
        <div className="loading">Loading orders...</div>
      </div>
    );
  }

  return (
    <div className="orders-container-app" onClick={handleClickOutside}>
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast-notification-fxk ${toast.type === 'success' ? 'toast-success-fxk' : 'toast-error-fxk'}`}>
          <div className="toast-content-fxk">
            <div className="toast-icon-fxk">
              {toast.type === 'success' ? '✓' : '✗'}
            </div>
            <div className="toast-message-fxk">{toast.message}</div>
          </div>
        </div>
      )}

      {/* Seller Confirmation Modal */}
      {showConfirmModal && selectedOrder && (
        <div className="modal-overlay_ffx">
          <div className="modal-content_ffx">
            <div className="modal-header_ffx">
              <h3>Confirm Seller Transaction</h3>
              <button
                className="close-button_ffx"
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedOrder(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body_ffx">
              <div className="confirmation-content">
                <AlertCircle size={48} className="warning-icon" />
                <h4>Are you sure you want to confirm this transaction?</h4>
                <p>This will mark the seller transaction as complete for order <strong>{selectedOrder.id}</strong>.</p>
                
                <div className="order-summary">
                  <div className="order-info">
                    <span><strong>Buyer:</strong> {selectedOrder.farmer}</span>
                    <span><strong>Total:</strong> ₦{selectedOrder.total.toFixed(2)}</span>
                    <span><strong>Your Earnings:</strong> ₦{selectedOrder.farmerEarnings.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer_ffx">
              <button
                className="cancel-button_ffx"
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedOrder(null);
                }}
                disabled={confirming}
              >
                Cancel
              </button>
              <button
                className="confirm-button_ffx"
                onClick={confirmSellerTransaction}
                disabled={confirming}
              >
                {confirming ? (
                  <>
                    <div className="loading-spinner"></div>
                    Confirming...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    Confirm Transaction
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="orders-header-app">
        <h2 className="orders-title-app">My Orders</h2>
        {totalEarnings > 0 && (
          <div className="total-earnings-app">
            <span>Total Earnings: ₦{totalEarnings.toFixed(2)}</span>
          </div>
        )}
      </div>

      
      <div className="orders-search-bar-app">
        <div className="search-wrapper-app">
          <div className="search-icon-app">
            <Search size={18} />
          </div>
          <input
            type="text"
            className="search-input-app"
            placeholder="Search orders by ID, farmer, or product"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filters-app">
          <div className="filter-select-wrapper-app">
            <label htmlFor="status-filter" className="filter-label-app">
              Status:
            </label>
            <select
              id="status-filter"
              className="filter-select-app"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="delivered">Delivered</option>
              <option value="canceled">Canceled</option>
            </select>
          </div>

          <button
            className="sort-button-app"
            onClick={toggleSortOrder}
            aria-label="Toggle sort order"
          >
            <Calendar size={16} />
            <span>Date</span>
            {sortOrder === "desc" ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronUp size={16} />
            )}
          </button>
        </div>
      </div>

      {/* Orders List */}
      <div className="orders-list-app">
        {sortedOrders.length > 0 ? (
          sortedOrders.map((order) => (
            <div key={order.id} className="order-card-app">
              <div
                className="order-header-app"
                onClick={() => toggleOrderExpansion(order.id)}
              >
                <div className="order-main-info-app">
                  <div className="order-id-wrapper-app">
                    <span className="order-id-label-app">Order ID:</span>
                    <span className="order-id-app">{order.id}</span>
                  </div>
                  <div className="order-farmer-app">{order.farmer}</div>
                </div>

                <div className="order-secondary-info-app">
                  <div
                    className={`${getStatusClass(
                      order.status
                    )} status-dropdown-container`}
                    onClick={(e) => toggleStatusDropdown(order.id, e)}
                  >
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    <ChevronDown size={14} className="ml-1" />

                    {activeStatusDropdowns[order.id] && (
                      <div className="order-change-app">
                        <span
                          className={`status-option ${
                            order.status === "delivered" ? "active" : ""
                          }`}
                          onClick={(e) =>
                            changeOrderStatus(order.id, "delivered", e)
                          }
                        >
                          Delivered
                        </span>
                        <span
                          className={`status-option ${
                            order.status === "processing" ? "active" : ""
                          }`}
                          onClick={(e) =>
                            changeOrderStatus(order.id, "processing", e)
                          }
                        >
                          Processing
                        </span>
                        <span
                          className={`status-option ${
                            order.status === "canceled" ? "active" : ""
                          }`}
                          onClick={(e) =>
                            changeOrderStatus(order.id, "canceled", e)
                          }
                        >
                          Canceled
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="order-date-app">
                    <Calendar size={14} />
                    <span>{new Date(order.date).toLocaleDateString()}</span>
                  </div>

                  <div className="order-total-app">
                    ₦{order.total.toFixed(2)}
                  </div>
                  <button
                    className="expand-button-app"
                    aria-label="Expand order details"
                  >
                    {expandedOrders[order.id] ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </button>
                </div>
              </div>

              {expandedOrders[order.id] && (
                <div className="order-details-app">
                  <div className="order-summary-info-app">
                    <div className="order-buyer-info">
                      <h4>Buyer Information</h4>
                      <p><strong>Name:</strong> {order.farmer}</p>
                      <p><strong>Email:</strong> {order.buyerEmail || 'N/A'}</p>
                      <p><strong>Phone:</strong> {order.phone || 'N/A'}</p>
                      <p><strong>Shipping Address:</strong> {order.shippingAddress || 'N/A'}</p>
                    </div>
                    <div className="order-transaction-status">
                      <h4>Transaction Status</h4>
                      <p><strong>Seller Confirmed:</strong> 
                        <span className={order.sellerConfirmed ? 'confirmed' : 'not-confirmed'}>
                          {order.sellerConfirmed ? ' ✓ Yes' : ' ✗ No'}
                        </span>
                      </p>
                      <p><strong>Buyer Confirmed:</strong> 
                        <span className={order.buyerConfirmed ? 'confirmed' : 'not-confirmed'}>
                          {order.buyerConfirmed ? ' ✓ Yes' : ' ✗ No'}
                        </span>
                      </p>
                      {!order.sellerConfirmed && (
                        <button
                          className="confirm-seller-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleConfirmSeller(order);
                          }}
                        >
                          <CheckCircle size={16} />
                          Confirm Seller Transaction
                        </button>
                      )}
                    </div>
                  </div>

                  <h4 className="order-items-title-app">Order Items</h4>
                  <table className="order-items-table-app">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>Unit Price</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.map((item, index) => (
                        <tr key={index}>
                          <td>{item.name}</td>
                          <td>
                            {item.quantity} {item.unit}
                          </td>
                          <td>₦{item.price.toFixed(2)}</td>
                          <td>₦{item.total.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="3" className="order-total-label-app">
                          Total
                        </td>
                        <td className="order-grand-total-app">
                          ₦{order.total.toFixed(2)}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="3" className="order-earnings-label-app">
                          Your Earnings
                        </td>
                        <td className="order-earnings-app">
                          ₦{order.farmerEarnings.toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="no-orders-app">
            <p>No orders found matching your search criteria.</p>
          </div>
        )}
      </div>

      <style jsx>{`
        .toast-notification-fxk {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          max-width: 400px;
          animation: slideInRight 0.3s ease-out;
        }

        .toast-success-fxk {
          background: #10b981;
          color: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .toast-error-fxk {
          background: #ef4444;
          color: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .toast-content-fxk {
          display: flex;
          align-items: center;
          padding: 16px;
          gap: 12px;
        }

        .toast-icon-fxk {
          font-size: 18px;
          font-weight: bold;
          flex-shrink: 0;
        }

        .toast-message-fxk {
          flex: 1;
          font-size: 14px;
          line-height: 1.4;
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .loading {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 200px;
          font-size: 16px;
          color: #6b7280;
        }

        .total-earnings-app {
          background: #10b981;
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 14px;
        }

        .order-summary-info-app {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 20px;
          padding: 20px;
          background: #f9fafb;
          border-radius: 8px;
        }
          @media (max-width: 640px) {
           .order-summary-info-app {
            grid-template-columns: 1fr;
           }
          }

        .order-buyer-info h4,
        .order-transaction-status h4 {
          margin: 0 0 12px 0;
          color: #374151;
          font-size: 16px;
        }

        .order-buyer-info p,
        .order-transaction-status p {
          margin: 8px 0;
          color: #6b7280;
        }

        .confirmed {
          color: #10b981;
          font-weight: 600;
        }

        .not-confirmed {
          color: #ef4444;
          font-weight: 600;
        }

        .confirm-seller-btn {
          background: #10b981;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          margin-top: 12px;
          transition: background-color 0.2s;
        }

        .confirm-seller-btn:hover {
          background: #059669;
        }

        .order-earnings-label-app {
          font-weight: 600;
          color: #10b981;
        }

        .order-earnings-app {
          font-weight: 600;
          color: #10b981;
        }

        .modal-overlay_ffx {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content_ffx {
          background: white;
          border-radius: 8px;
          max-width: 500px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header_ffx {
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header_ffx h3 {
          margin: 0;
          color: #374151;
        }

        .close-button_ffx {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #6b7280;
          padding: 0;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-button_ffx:hover {
          color: #374151;
        }

        .modal-body_ffx {
          padding: 20px;
        }

        .confirmation-content {
          text-align: center;
        }

        .warning-icon {
          color: #f59e0b;
          margin-bottom: 16px;
        }

        .confirmation-content h4 {
          margin: 16px 0 12px 0;
          color: #374151;
        }

        .confirmation-content p {
          margin: 0 0 20px 0;
          color: #6b7280;
          line-height: 1.5;
        }

        .order-summary {
          background: #f9fafb;
          padding: 16px;
          border-radius: 6px;
          margin: 20px 0;
        }

        .order-info {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .order-info span {
          color: #374151;
        }

        .modal-footer_ffx {
          padding: 20px;
          border-top: 1px solid #e5e7eb;
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .cancel-button_ffx {
          background: #6b7280;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
        }

        .cancel-button_ffx:hover {
          background: #4b5563;
        }

        .confirm-button_ffx {
          background: #10b981;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .confirm-button_ffx:hover {
          background: #059669;
        }

        .confirm-button_ffx:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .loading-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #ffffff;
          border-top: 2px solid transparent;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
